import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";

export default function Pricing() {
  const { user } = useAuth();
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/pricing").then((r) => setPrices(r.data)).catch(() => {});
  }, []);

  const start = async (lookup_key) => {
    if (!user) { navigate("/auth?mode=register"); return; }
    setLoading(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
        user_id: user.id,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setLoading("");
    }
  };

  const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;
  const monthly = prices.find((p) => p.lookup_key === "rooted_monthly");

  return (
    <div className="min-h-screen">
      <header className="max-w-[1400px] mx-auto px-6 md:px-10 h-24 flex items-center justify-between border-b border-border">
        <Link to="/" className="flex items-center" data-testid="pricing-logo"><Logo size={80} /></Link>
        {user ? <Link to="/app" className="text-sm text-stone-600 hover:text-[hsl(var(--foreground))]">Back to practice</Link> :
          <Link to="/auth?mode=login" className="text-sm text-stone-600 hover:text-[hsl(var(--foreground))]">Sign in</Link>}
      </header>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="label-eyebrow mb-6">Membership</div>
        <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight max-w-3xl">
          One simple <em className="italic text-[hsl(var(--primary))]">monthly</em>{" "}
          membership. A whole year of coming home.
        </h1>
        <p className="mt-8 max-w-2xl text-stone-600 leading-relaxed">
          Everything Energy Awakens offers, month after month — nourishing meals, movement,
          daily coaching, private measurements, and the community conversation.
        </p>

        <div className="mt-16 max-w-2xl border-t border-b border-border" data-testid="pricing-plans">
          <div className="p-10 md:p-12" data-testid="plan-monthly">
            <div className="label-eyebrow mb-3">Monthly</div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="font-serif text-6xl md:text-7xl" data-testid="monthly-price">{monthly ? fmt(monthly.amount) : "$9.99"}</span>
              <span className="text-stone-500">/ month</span>
            </div>
            <div className="text-sm text-stone-500 mb-8">Rolling. Cancel anytime, come back anytime. Waitlist members keep this rate for life.</div>
            <ul className="space-y-3 mb-10 text-sm">
              {[
                "Daily meals, movement, and coaching — every month of the year",
                "Each month's theme, recipes, and movements as they unlock",
                "Body measurements tracker with 6 honest zones",
                "Community comments, photos, likes",
                "Cancel anytime — keep what you've kept",
              ].map((f) => (
                <li key={f} className="flex gap-3 items-start"><Check className="w-4 h-4 text-[hsl(var(--primary))] mt-0.5" strokeWidth={2} />{f}</li>
              ))}
            </ul>
            <button
              onClick={() => start("rooted_monthly")}
              disabled={loading === "rooted_monthly"}
              data-testid="btn-buy-monthly"
              className="inline-flex items-center gap-2 bg-[hsl(var(--primary))] text-white px-8 py-4 hover:bg-[hsl(var(--foreground))] transition-colors disabled:opacity-60"
            >
              {loading === "rooted_monthly" ? "Redirecting…" : "Begin monthly"}
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        <p className="text-xs text-stone-500 mt-8 italic">
          Test mode. Use card 4242 4242 4242 4242 with any future expiry &amp; CVC.
        </p>
        <p className="text-sm text-stone-600 mt-6">
          Already a member? <Link to="/auth?mode=login" className="underline text-[hsl(var(--primary))] hover:text-[hsl(var(--foreground))]" data-testid="pricing-signin-link">Sign in</Link>
        </p>
      </section>
    </div>
  );
}
